<template>
  <div class="min-h-full bg-transparent text-slate-900 dark:text-[#F1F5F9] pb-16">
    <div class="max-w-7xl mx-auto space-y-6">
      <!-- Executive Control Bar -->
      <div
        class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/90 dark:border-[#263449] p-4 md:p-5 shadow-xs transition-all duration-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3.5"
      >
        <div class="flex items-center gap-3">
          <div
            class="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-tr from-indigo-600 to-violet-500 text-white flex items-center justify-center shadow-md shadow-indigo-500/20 shrink-0"
          >
            <School class="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 class="text-base sm:text-xl font-black text-slate-900 dark:text-white tracking-tight">
              Class Management
            </h1>
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Organize academic sections, rosters, and fee structures
            </p>
          </div>
        </div>

        <button
          @click="switchView(viewMode === 'list' ? 'create' : 'list')"
          class="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs sm:text-sm font-semibold shadow-xs transition-colors shrink-0 cursor-pointer"
        >
          <template v-if="viewMode === 'list'">
            <Plus class="w-4 h-4" />
            <span>New Class</span>
          </template>
          <template v-else>
            <List class="w-4 h-4" />
            <span>View All Classes</span>
          </template>
        </button>
      </div>

    <!-- List -->
    <div
      v-if="viewMode === 'list'"
      class="max-w-7xl mx-auto"
    >
      <!-- Premium Class Loading Overlay -->
      <AppLoader
        :isVisible="isLoadingClasses"
        variant="fullscreen"
        message="Loading Classroom Rosters..."
        subtext="Fetching sections, classes, and assigned students..."
        color="indigo"
        icon="cap"
      />

      <div
        v-if="
          classes.length === 1 &&
          ['passout', 'Passout'].includes(classes[0].name)
        "
        class="text-center py-20"
      >
        <div
          class="bg-white dark:bg-[#111C30] rounded-xl p-10 shadow-sm border border-slate-200 dark:border-[#263449] max-w-md mx-auto"
        >
          <School class="w-14 h-14 text-slate-300 dark:text-[#7E8DA5] mx-auto mb-5" />
          <h3 class="text-lg sm:text-xl font-semibold text-slate-800 dark:text-[#F1F5F9] mb-2">
            No classes yet
          </h3>
          <p class="text-slate-600 dark:text-[#A6B4C8] mb-5">
            Create your first class to get started
          </p>
          <div class="flex gap-3 justify-center">
            <button
              @click="switchView('create')"
              class="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-medium transition-all duration-200 flex items-center gap-2"
            >
              <Plus class="w-5 h-5" />
              Create Class
            </button>
            <button
              @click="showEmptyStateCreateDialog = true"
              class="bg-slate-100 dark:bg-[#18243A] hover:bg-slate-200 dark:hover:bg-[#172235] text-slate-700 dark:text-[#F1F5F9] border border-transparent dark:border-[#263449] px-5 py-2.5 rounded-xl font-medium transition-all duration-200 flex items-center gap-2"
            >
              <Users class="w-5 h-5" />
              Bulk Create
            </button>
          </div>
        </div>
      </div>

      <div
        v-else
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        <div
          v-for="cls in classes"
          :key="cls.id || cls._id"
          :class="[
            'rounded-2xl p-6 border transition-all duration-300 relative',
            cls.name && cls.name.toLowerCase() === 'passout'
              ? 'bg-slate-50 dark:bg-[#18243A] border-slate-300 dark:border-[#263449] shadow-none hover:shadow-none hover:translate-y-0'
              : 'bg-white dark:bg-[#111C30] shadow-sm border-slate-200 dark:border-[#263449] hover:shadow-xl hover:-translate-y-1',
          ]"
        >
          <!-- 3-dot menu -->
          <div v-if="cls.name.toLowerCase() !== 'passout'">
            <div class="absolute top-4 right-4">
              <div
                class="relative"
                @click.stop="toggleMenu(cls._id)"
              >
                <MoreVertical
                  class="w-5 h-5 text-slate-500 dark:text-[#A6B4C8] cursor-pointer hover:text-slate-700 dark:hover:text-[#F1F5F9]"
                />

                <div
                  v-if="openMenuId === cls._id"
                  class="absolute right-0 mt-2 w-44 bg-white dark:bg-[#18243A] rounded-lg shadow-lg border border-slate-200 dark:border-[#263449] z-50 py-1"
                >
                  <button
                    @click="startEdit(cls)"
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-[#F1F5F9] hover:bg-blue-50 dark:hover:bg-[#172235] hover:text-blue-600 transition"
                  >
                    <Edit class="w-4 h-4 text-blue-600 dark:text-blue-400" /> Edit
                  </button>
                  <button
                    @click="
                      openFeeModal(cls);
                      closeMenu();
                    "
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-[#F1F5F9] hover:bg-purple-50 dark:hover:bg-[#172235] hover:text-purple-600 transition"
                  >
                    <Sliders class="w-4 h-4 text-purple-600 dark:text-purple-400" /> Configure Fee
                  </button>
                  <button
                    @click="
                      confirmDelete(cls);
                      closeMenu();
                    "
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-[#F1F5F9] hover:bg-red-50 dark:hover:bg-[#172235] hover:text-red-600 transition"
                  >
                    <Trash2 class="w-4 h-4 text-red-600 dark:text-rose-400" /> Delete
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Header -->
          <div class="flex items-start gap-4 mb-5">
            <div
              :class="[
                'w-12 h-12 rounded-xl flex items-center justify-center shadow-inner',
                cls.name && cls.name.toLowerCase() === 'passout'
                  ? 'bg-slate-100 dark:bg-[#18243A]'
                  : 'bg-gradient-to-r from-blue-100 to-blue-200 dark:from-blue-950 dark:to-indigo-950',
              ]"
            >
              <BookOpen
                :class="[
                  'w-6 h-6',
                  cls.name && cls.name.toLowerCase() === 'passout'
                    ? 'text-slate-500 dark:text-[#7E8DA5]'
                    : 'text-blue-600 dark:text-blue-400',
                ]"
              />
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="font-semibold text-slate-800 dark:text-[#F1F5F9] text-lg truncate">
                {{ cls.name }}
              </h3>
              <div v-if="cls.name.toLowerCase() !== 'passout'">
                <p
                  class="text-xs inline-block mt-1 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] border border-transparent dark:border-[#263449] font-medium"
                >
                  {{ cls.section || "No section" }}
                </p>
              </div>
            </div>
          </div>

          <!-- Stats -->
          <div class="grid grid-cols-2 gap-4 mb-4">
            <div class="bg-slate-50 dark:bg-[#18243A] p-3 rounded-lg text-center border border-transparent dark:border-[#263449]">
              <p class="text-xl font-bold text-slate-800 dark:text-[#F1F5F9]">
                {{ cls.totalStudents }}
              </p>
              <p class="text-xs text-slate-500 dark:text-[#7E8DA5]">Total Students</p>
            </div>
            <div v-if="cls.name.toLowerCase() !== 'passout'">
              <div class="bg-green-50 dark:bg-emerald-950/30 p-3 rounded-lg text-center border border-transparent dark:border-emerald-900/30">
                <p class="text-xl font-bold text-green-700 dark:text-emerald-400">
                  {{ cls.totalPresentToday }}
                </p>
                <p class="text-xs text-slate-500 dark:text-[#7E8DA5]">Present Today</p>
              </div>
            </div>
          </div>

          <!-- Fees Information -->
          <div
            v-if="cls.name && cls.name.toLowerCase() !== 'passout'"
            class="mb-5 p-3 rounded-xl bg-slate-50 dark:bg-[#18243A] border border-slate-200/80 dark:border-[#263449] space-y-1.5"
          >
            <div class="flex items-center justify-between text-xs">
              <span class="text-slate-500 dark:text-[#A6B4C8] font-medium flex items-center gap-1.5">
                <GraduationCap class="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                Admission Fee:
              </span>
              <span class="font-bold text-slate-800 dark:text-[#F1F5F9]">
                ₹{{ (cls.admissionFee || 0).toLocaleString("en-IN") }}
              </span>
            </div>
            <div class="flex items-center justify-between text-xs">
              <span class="text-slate-500 dark:text-[#A6B4C8] font-medium flex items-center gap-1.5">
                <Calendar class="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                Monthly Fee:
              </span>
              <span class="font-bold text-indigo-600 dark:text-indigo-400">
                ₹{{ (cls.monthlyFee || 0).toLocaleString("en-IN") }}<span class="text-[10px] text-slate-400 dark:text-[#7E8DA5] font-normal">/mo</span>
              </span>
            </div>
          </div>

          <!-- Footer -->
          <div class="flex items-center justify-between gap-2 pt-2 border-t border-slate-100 dark:border-[#263449]">
            <button
              v-if="cls.name && cls.name.toLowerCase() !== 'passout'"
              type="button"
              @click="openFeeModal(cls)"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-indigo-200 dark:border-indigo-800/40 bg-indigo-50/70 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 text-xs font-semibold transition-all"
              title="Configure admission & monthly fees"
            >
              <Sliders class="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
              Fee Setup
            </button>
            <span v-else></span>
            <router-link
              :to="{ path: '/students', query: { classId: cls._id || cls.id } }"
              class="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium shadow transition-all"
            >
              <Users class="w-3.5 h-3.5" />
              View Students
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Create -->
    <div
      v-else-if="viewMode === 'create'"
      class="max-w-xl mx-auto"
    >
      <div
        class="bg-white dark:bg-[#111C30] rounded-2xl p-6 sm:p-8 w-full shadow-sm border border-slate-200/90 dark:border-[#263449]"
      >
        <div class="text-center mb-6">
          <div
            class="w-12 h-12 bg-indigo-50 dark:bg-indigo-950/70 border border-indigo-100 dark:border-indigo-900/50 rounded-2xl flex items-center justify-center mx-auto mb-3 text-indigo-600 dark:text-indigo-400"
          >
            <Plus class="w-6 h-6" />
          </div>
          <h2 class="text-xl font-bold text-slate-900 dark:text-white tracking-tight mb-1">
            Create New Class
          </h2>
          <p class="text-slate-500 dark:text-slate-400 text-xs sm:text-sm">
            Add an academic class tier and configure its fee structure
          </p>
        </div>

        <form
          @submit.prevent="createClass"
          class="space-y-4 sm:space-y-5"
        >
          <div>
            <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
              Class Name <span class="text-rose-500">*</span>
            </label>
            <input
              type="text"
              v-model="newClassName"
              required
              class="w-full px-4 py-2.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-900 dark:text-white placeholder-slate-400 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600 transition"
              placeholder="e.g., Grade 10 - Science"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2"
              >Section (Optional)</label
            >
            <input
              type="text"
              v-model="newClassSection"
              class="w-full px-4 py-2.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-900 dark:text-white placeholder-slate-400 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600 transition"
              placeholder="e.g., Section A"
            />
          </div>

          <!-- Fee Settings for New Class -->
          <div class="p-4 bg-slate-50/70 dark:bg-[#18243A]/60 rounded-xl border border-slate-200/80 dark:border-[#263449] space-y-3">
            <h4 class="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide flex items-center gap-1.5">
              <Sliders class="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              Class Fee Structure (Optional)
            </h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1 flex items-center gap-1">
                  <GraduationCap class="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                  Admission Fee (₹)
                </label>
                <div class="relative">
                  <span class="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 font-bold text-sm">₹</span>
                  <input
                    type="number"
                    v-model.number="newAdmissionFee"
                    min="0"
                    step="1"
                    placeholder="0"
                    class="w-full pl-7 pr-3 py-2 bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/30 text-slate-900 dark:text-white text-sm font-semibold"
                  />
                </div>
                <p class="text-[10px] text-slate-400 mt-1">One-time admission charge</p>
              </div>

              <div>
                <label class="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1 flex items-center gap-1">
                  <Calendar class="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                  Monthly Fee (₹)
                </label>
                <div class="relative">
                  <span class="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 font-bold text-sm">₹</span>
                  <input
                    type="number"
                    v-model.number="newMonthlyFee"
                    min="0"
                    step="1"
                    placeholder="0"
                    class="w-full pl-7 pr-3 py-2 bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/30 text-slate-900 dark:text-white text-sm font-semibold"
                  />
                </div>
                <p class="text-[10px] text-slate-400 mt-1">Recurring fee per month</p>
              </div>
            </div>
          </div>

          <button
            type="submit"
            :disabled="isSubmitting"
            class="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-2.5 rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer shadow-xs"
          >
            <Loader2
              v-if="isSubmitting"
              class="w-5 h-5 animate-spin"
            />
            <span v-else>Create Class</span>
          </button>
        </form>
      </div>
    </div>

    <!-- Edit -->
    <div
      v-else-if="viewMode === 'edit'"
      class="max-w-xl mx-auto"
    >
      <div
        class="bg-white dark:bg-[#111C30] rounded-2xl p-6 sm:p-8 w-full shadow-sm border border-slate-200/90 dark:border-[#263449]"
      >
        <div class="text-center mb-6">
          <div
            class="w-12 h-12 bg-emerald-50 dark:bg-emerald-950/70 border border-emerald-100 dark:border-emerald-900/50 rounded-2xl flex items-center justify-center mx-auto mb-3 text-emerald-600 dark:text-emerald-400"
          >
            <Edit class="w-6 h-6" />
          </div>
          <h2 class="text-xl font-bold text-slate-900 dark:text-white tracking-tight mb-1">Edit Class</h2>
          <p class="text-slate-500 dark:text-slate-400 text-xs sm:text-sm">Update class roster configuration and fee details</p>
        </div>

        <form
          @submit.prevent="editClass"
          class="space-y-4 sm:space-y-5"
        >
          <div>
            <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
              Class Name <span class="text-rose-500">*</span>
            </label>
            <input
              type="text"
              v-model="editClassName"
              required
              class="w-full px-4 py-2.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-900 dark:text-white placeholder-slate-400 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-600 transition"
              placeholder="e.g., Mathematics Grade 10"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2"
              >Section (Optional)</label
            >
            <input
              type="text"
              v-model="editClassSection"
              class="w-full px-4 py-2.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-900 dark:text-white placeholder-slate-400 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-600 transition"
              placeholder="e.g., Section A"
            />
          </div>

          <!-- Fee Settings for Editing Class -->
          <div class="p-4 bg-slate-50/70 dark:bg-[#18243A]/60 rounded-xl border border-slate-200/80 dark:border-[#263449] space-y-3">
            <h4 class="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide flex items-center gap-1.5">
              <Sliders class="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              Class Fee Structure
            </h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1 flex items-center gap-1">
                  <GraduationCap class="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                  Admission Fee (₹)
                </label>
                <div class="relative">
                  <span class="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 font-bold text-sm">₹</span>
                  <input
                    type="number"
                    v-model.number="editAdmissionFee"
                    min="0"
                    step="1"
                    placeholder="0"
                    class="w-full pl-7 pr-3 py-2 bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/30 text-slate-900 dark:text-white text-sm font-semibold"
                  />
                </div>
                <p class="text-[10px] text-slate-400 mt-1">One-time admission charge</p>
              </div>

              <div>
                <label class="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1 flex items-center gap-1">
                  <Calendar class="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                  Monthly Fee (₹)
                </label>
                <div class="relative">
                  <span class="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 font-bold text-sm">₹</span>
                  <input
                    type="number"
                    v-model.number="editMonthlyFee"
                    min="0"
                    step="1"
                    placeholder="0"
                    class="w-full pl-7 pr-3 py-2 bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/30 text-slate-900 dark:text-white text-sm font-semibold"
                  />
                </div>
                <p class="text-[10px] text-slate-400 mt-1">Recurring fee per month</p>
              </div>
            </div>

            <!-- Apply Fee Changes Checkbox -->
            <div class="p-3 bg-slate-50 dark:bg-[#18243A] rounded-xl border border-slate-200/90 dark:border-[#263449] mt-2">
              <label class="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  v-model="editApplyToAllStudents"
                  class="mt-0.5 rounded text-emerald-600 focus:ring-emerald-500 h-4 w-4 border-slate-300 dark:border-slate-600"
                />
                <div>
                  <span class="text-xs font-semibold text-slate-800 dark:text-slate-200 block">
                    Apply fee changes to enrolled students
                  </span>
                  <p class="text-[11px] text-slate-500 dark:text-slate-400 leading-tight mt-0.5">
                    Updates admission fee target and future monthly billing for current students in this class.
                  </p>
                </div>
              </label>
            </div>
          </div>

          <div class="flex gap-2.5 pt-2">
            <button
              type="button"
              @click="cancelEdit"
              class="flex-1 bg-slate-100 hover:bg-slate-200 dark:bg-[#18243A] dark:hover:bg-[#1F2E49] text-slate-700 dark:text-slate-300 py-2.5 rounded-xl font-semibold text-xs sm:text-sm transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="isSubmitting"
              class="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-2.5 rounded-xl font-bold text-xs sm:text-sm shadow-sm shadow-indigo-600/20 transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer"
            >
              <Loader2
                v-if="isSubmitting"
                class="w-4 h-4 animate-spin"
              />
              <span v-else>Save Changes</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Dialog (KEPT) -->
    <Transition name="modal">
      <div
        v-if="showConfirmationDialog"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div
          class="absolute inset-0 bg-black/20 backdrop-blur-sm"
          @click="handleConfirmation(false)"
        ></div>
        <div
          class="relative bg-white rounded-xl p-6 w-full max-w-sm shadow-md border border-slate-200"
        >
          <div class="text-center">
            <div
              class="w-14 h-14 mx-auto mb-4 rounded-xl flex items-center justify-center bg-red-100"
            >
              <Trash2 class="w-7 h-7 text-red-600" />
            </div>
            <h3 class="text-lg font-semibold text-slate-800 mb-2">
              {{ confirmationTitle }}
            </h3>
            <p class="text-slate-600 mb-5 text-sm">{{ confirmationMessage }}</p>
            <div class="flex gap-3">
              <button
                @click="handleConfirmation(false)"
                class="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-2.5 rounded-lg font-medium transition-all duration-200"
              >
                Cancel
              </button>
              <button
                @click="handleConfirmation(true)"
                class="flex-1 py-2.5 rounded-lg font-medium text-white transition-all duration-200 bg-red-600 hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Empty state bulk create dialog -->
    <Transition name="modal">
      <div
        v-if="showEmptyStateCreateDialog"
        class="fixed inset-0 z-[1005] flex items-center justify-center p-4"
      >
        <div class="absolute inset-0 bg-black/20 backdrop-blur-sm"></div>
        <div
          class="relative bg-white rounded-xl p-6 w-full max-w-5xl shadow-md border border-slate-200 max-h-[90vh] overflow-y-auto"
        >
          <div class="mb-6">
            <div class="flex items-center gap-3 mb-2">
              <Users class="w-6 h-6 text-blue-600" />
              <h3 class="text-xl font-bold text-slate-800">Create Classes</h3>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <!-- Left: defaults with per-class sections -->
            <div class="md:col-span-2">
              <div class="flex items-center justify-between mb-3">
                <span class="text-sm font-medium text-slate-700"
                  >Default Classes (1–10)</span
                >
                <label
                  class="flex items-center gap-2 text-sm text-slate-600 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    v-model="bulkSelectAll"
                    @change="handleToggleAll"
                  />
                  Select all
                </label>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div
                  v-for="tpl in bulkTemplates"
                  :key="tpl.id"
                  class="rounded-lg border border-slate-200 p-3"
                >
                  <div class="flex items-center justify-between gap-2">
                    <label class="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        v-model="tpl.selected"
                        @change="handleToggleOne"
                      />
                      <span class="text-sm text-slate-700">{{ tpl.name }}</span>
                    </label>
                    <button
                      type="button"
                      @click.stop="tpl.adding = !tpl.adding"
                      class="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border border-blue-200 px-2 py-1 rounded-md text-xs"
                      :aria-expanded="tpl.adding ? 'true' : 'false'"
                      :aria-label="`Add section to ${tpl.name}`"
                    >
                      <Plus class="w-4 h-4" />
                      Add section
                    </button>
                  </div>

                  <div
                    v-if="tpl.adding"
                    class="flex items-center gap-2 mt-2"
                  >
                    <input
                      v-model="tpl.newSection"
                      @keyup.enter="addSection(tpl)"
                      type="text"
                      class="w-full px-2 py-1 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., A"
                    />
                    <button
                      type="button"
                      @click.stop="addSection(tpl)"
                      class="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs"
                    >
                      Add
                    </button>
                  </div>

                  <div
                    v-if="tpl.sections.length"
                    class="flex flex-wrap gap-2 mt-2"
                  >
                    <span
                      v-for="(sec, idx) in tpl.sections"
                      :key="sec + idx"
                      class="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-2 py-1 rounded text-xs"
                    >
                      <span>Section {{ sec }}</span>
                      <button
                        type="button"
                        @click.stop="removeSection(tpl, idx)"
                        class="text-slate-500 hover:text-red-600"
                        :aria-label="`Remove section ${sec} from ${tpl.name}`"
                      >
                        <X class="w-3 h-3" />
                      </button>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right: global section + fee defaults + custom class rows -->
            <div class="space-y-5">
              <!-- Global section -->
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-1.5"
                  >Section (optional)</label
                >
                <input
                  v-model="bulkSection"
                  type="text"
                  class="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Section A"
                />
                <p class="text-[11px] text-slate-500 mt-1">
                  Per-class sections override this global section.
                </p>
              </div>

              <!-- Default Fee Structure for Bulk Classes -->
              <div class="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-2.5">
                <label class="block text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                  <Sliders class="w-3.5 h-3.5 text-blue-600" />
                  Default Fees (Optional)
                </label>
                <div class="grid grid-cols-2 gap-2.5">
                  <div>
                    <label class="block text-[11px] text-slate-600 mb-1 flex items-center gap-1">
                      <GraduationCap class="w-3 h-3 text-indigo-600" />
                      Admission Fee (₹)
                    </label>
                    <div class="relative">
                      <span class="absolute inset-y-0 left-0 pl-2.5 flex items-center text-slate-400 font-bold text-xs">₹</span>
                      <input
                        v-model.number="bulkAdmissionFee"
                        type="number"
                        min="0"
                        step="1"
                        placeholder="0"
                        class="w-full pl-6 pr-2 py-1.5 text-xs bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                      />
                    </div>
                  </div>
                  <div>
                    <label class="block text-[11px] text-slate-600 mb-1 flex items-center gap-1">
                      <Calendar class="w-3 h-3 text-purple-600" />
                      Monthly Fee (₹)
                    </label>
                    <div class="relative">
                      <span class="absolute inset-y-0 left-0 pl-2.5 flex items-center text-slate-400 font-bold text-xs">₹</span>
                      <input
                        v-model.number="bulkMonthlyFee"
                        type="number"
                        min="0"
                        step="1"
                        placeholder="0"
                        class="w-full pl-6 pr-2 py-1.5 text-xs bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                      />
                    </div>
                  </div>
                </div>
                <p class="text-[10px] text-slate-500">
                  Applies to all selected default classes and custom classes.
                </p>
              </div>

              <!-- Custom Classes -->
              <div>
                <div class="flex items-center justify-between mb-2">
                  <h4 class="text-sm font-medium text-slate-700">
                    Custom Classes
                  </h4>
                  <button
                    type="button"
                    @click="addCustomClassRow"
                    class="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border border-blue-200 px-2 py-1 rounded-md text-xs"
                    aria-label="Add more class"
                  >
                    <Plus class="w-4 h-4" />
                    Add more class
                  </button>
                </div>

                <div
                  v-if="!customClasses.length"
                  class="text-xs text-slate-500"
                >
                  Click "Add more class" to add your own class names.
                </div>

                <div class="space-y-3">
                  <div
                    v-for="(row, idx) in customClasses"
                    :key="idx"
                    class="rounded-lg border border-slate-200 p-3"
                  >
                    <div class="flex items-start gap-2">
                      <div class="flex-1">
                        <label class="block text-[11px] text-slate-600 mb-1"
                          >Class name</label
                        >
                        <input
                          v-model="row.name"
                          type="text"
                          class="w-full px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs"
                          placeholder="e.g., Physics 12"
                        />
                      </div>
                      <div class="w-24">
                        <label class="block text-[11px] text-slate-600 mb-1"
                          >Section</label
                        >
                        <input
                          v-model="row.section"
                          type="text"
                          class="w-full px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs"
                          placeholder="e.g., B"
                        />
                      </div>
                      <div class="w-24">
                        <label class="block text-[11px] text-slate-600 mb-1"
                          >Monthly (₹)</label
                        >
                        <input
                          v-model.number="row.monthlyFee"
                          type="number"
                          min="0"
                          step="1"
                          placeholder="Default"
                          class="w-full px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs"
                        />
                      </div>
                      <button
                        type="button"
                        @click="removeCustomClassRow(idx)"
                        class="mt-6 text-slate-500 hover:text-red-600 p-1.5 rounded-md"
                        aria-label="Remove custom class"
                      >
                        <X class="w-4 h-4" />
                      </button>
                    </div>
                    <p
                      v-if="rowError(idx)"
                      class="mt-2 text-[11px] text-red-600"
                    >
                      Class name is required.
                    </p>
                  </div>
                </div>
                <p class="text-[11px] text-slate-500 mt-2">
                  Leave Section or Fee empty to use the global defaults above.
                </p>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex flex-col sm:flex-row gap-3 mt-6">
            <button
              class="sm:ml-auto bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-4 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 disabled:bg-slate-300"
              :disabled="isSubmitting || selectedCount === 0"
              @click="handleBulkCreate"
            >
              <Loader2
                v-if="isSubmitting"
                class="w-5 h-5 animate-spin"
              />
              <template v-else>
                <Plus class="w-5 h-5" />
                Create {{ selectedCount }} selected
              </template>
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Configure Class Fee Modal -->
    <ConfigureClassFeeModal
      :show="showClassFeeModal"
      :initialClassId="feeModalClassId"
      :classes="activeClasses"
      :isSubmitting="isConfiguringClassFee"
      @close="showClassFeeModal = false"
      @save="handleSaveClassFeeConfiguration"
    />
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch } from "vue";
  import { useStore } from "vuex";
  import {
    Plus,
    List,
    School,
    BookOpen,
    MoreVertical,
    Loader2,
    Edit,
    Trash2,
    Users,
    X,
    GraduationCap,
    Calendar,
    Sliders,
  } from "lucide-vue-next";
  import ConfigureClassFeeModal from "@/components/FeeManagement/ConfigureClassFeeModal.vue";
  import { configureClassFee } from "@/services/feeService";
  import { useGlobalToast } from "@/utils/useGlobalToast";
  const { showToast } = useGlobalToast();
  import { useRouter } from "vue-router";

  const store = useStore();
  const router = useRouter();

  const TotalTeachers = computed(
    () => store.getters.getOnboardingTotalTeachers,
  );
  const TotalStudents = computed(
    () => store.getters.getOnboardingTotalStudents,
  );

  const refreshTeachersAndStudents = async () => {
    try {
      // Fetch the total teacher count for onboarding
      await store.dispatch("fetchTotalTeacherCountOnboarding");

      // Fetch the total student count for onboarding
      await store.dispatch("fetchTotalStudentCountOnboarding");
    } catch (error) {
      console.error("❌ Error refreshing teachers:", error);
    }
  };

  const viewMode = ref("list");
  const newClassName = ref("");
  const newClassSection = ref("");
  const newAdmissionFee = ref(0);
  const newMonthlyFee = ref(0);

  const editClassName = ref("");
  const editClassSection = ref("");
  const editAdmissionFee = ref(0);
  const editMonthlyFee = ref(0);
  const editApplyToAllStudents = ref(true);

  const editingClass = ref(null);
  const isSubmitting = ref(false);
  const showConfirmationDialog = ref(false);
  const isLoadingClasses = ref(false);
  const confirmationType = ref("");
  const confirmationTitle = ref("");
  const confirmationMessage = ref("");
  const classToDelete = ref(null);

  // Fee modal state
  const showClassFeeModal = ref(false);
  const feeModalClassId = ref("");
  const isConfiguringClassFee = ref(false);

  const showEmptyStateCreateDialog = ref(false);
  const hasShownEmptyDialog = ref(false);
  const openMenuId = ref(null);

  function toggleMenu(id) {
    openMenuId.value = openMenuId.value === id ? null : id;
  }

  function closeMenu() {
    openMenuId.value = null;
  }

  // Close menu when clicking outside
  document.addEventListener("click", () => {
    openMenuId.value = null;
  });

  // Default templates with optional per-class sections
  const bulkTemplates = ref(
    Array.from({ length: 10 }, (_, i) => ({
      id: i + 1,
      name: `Class ${i + 1}`,
      selected: true,
      sections: [],
      adding: false,
      newSection: "",
    })),
  );
  const bulkSelectAll = ref(true);
  const bulkSection = ref("");
  const bulkAdmissionFee = ref("");
  const bulkMonthlyFee = ref("");

  // Custom class rows for "Add more class"
  const customClasses = ref([]); // [{ name: '', section: '', monthlyFee: '' }]

  const classes = computed(() => {
    const list = store.getters.allClasses || [];

    // Separate "passout" classes
    const passoutClasses = list.filter(
      (cls) => cls.name?.toLowerCase() === "passout",
    );
    const normalClasses = list.filter(
      (cls) => cls.name?.toLowerCase() !== "passout",
    );

    // Sort normal classes numerically by the number in the name
    normalClasses.sort((a, b) => {
      const numA = parseInt(a.name.replace(/\D/g, "")) || 0;
      const numB = parseInt(b.name.replace(/\D/g, "")) || 0;
      return numA - numB;
    });

    // Put passout classes at the end
    return [...normalClasses, ...passoutClasses];
  });

  const activeClasses = computed(() =>
    classes.value.filter((cls) => cls.name?.toLowerCase() !== "passout"),
  );

  const openFeeModal = (cls) => {
    feeModalClassId.value = cls._id || cls.id;
    showClassFeeModal.value = true;
  };

  const handleSaveClassFeeConfiguration = async (payload) => {
    try {
      isConfiguringClassFee.value = true;
      const res = await configureClassFee(payload.classId, payload);
      if (res.data?.success) {
        showToast(
          "success",
          res.data.message || "Class fee structure saved and applied!",
        );
        showClassFeeModal.value = false;
        await store.dispatch("fetchAllClasses");
      }
    } catch (err) {
      console.error("handleSaveClassFeeConfiguration error:", err);
      const msg =
        err.response?.data?.message || "Failed to configure class fee";
      showToast("error", msg);
    } finally {
      isConfiguringClassFee.value = false;
    }
  };

  // Count considering per-class sections and custom rows
  const defaultsSelectedCount = computed(() =>
    bulkTemplates.value
      .filter((t) => t.selected)
      .reduce(
        (sum, t) => sum + (t.sections.length > 0 ? t.sections.length : 1),
        0,
      ),
  );

  // Only count custom rows that have a class name
  const customValidCount = computed(
    () => customClasses.value.filter((r) => (r.name || "").trim()).length,
  );

  const selectedCount = computed(
    () => defaultsSelectedCount.value + customValidCount.value,
  );

  onMounted(async () => {
    try {
      isLoadingClasses.value = true;
      await store.dispatch("fetchAllClasses");
    } catch (error) {
      console.error("Failed to load classes:", error);
    } finally {
      isLoadingClasses.value = false;
    }
  });

  watch(
    () => ({ loading: isLoadingClasses.value, list: classes.value }),
    ({ loading, list }) => {
      if (loading || !Array.isArray(list)) return;

      // Filter out "passout" class if it's the only one
      const validClasses = list.filter(
        (cls) => cls?.name?.toLowerCase() !== "passout",
      );

      if (validClasses.length === 0 && !hasShownEmptyDialog.value) {
        showEmptyStateCreateDialog.value = true;
        hasShownEmptyDialog.value = true;
      }
    },
  );

  const switchView = (mode) => {
    viewMode.value = mode;
    if (mode === "create") {
      newClassName.value = "";
      newClassSection.value = "";
      newAdmissionFee.value = 0;
      newMonthlyFee.value = 0;
    } else if (mode === "list") {
      editingClass.value = null;
    }
  };

  const startEdit = (cls) => {
    editingClass.value = cls;
    editClassName.value = cls.name;
    editClassSection.value = cls.section || "";
    editAdmissionFee.value = cls.admissionFee ?? 0;
    editMonthlyFee.value = cls.monthlyFee ?? 0;
    editApplyToAllStudents.value = true;
    closeMenu(); // Close menu immediately
    // Use nextTick to ensure menu closes before switching view
    setTimeout(() => {
      viewMode.value = "edit";
    }, 50);
  };

  const cancelEdit = () => {
    editingClass.value = null;
    editClassName.value = "";
    editClassSection.value = "";
    editAdmissionFee.value = 0;
    editMonthlyFee.value = 0;
    editApplyToAllStudents.value = true;
    viewMode.value = "list";
  };

  const confirmDelete = (cls) => {
    classToDelete.value = cls;
    confirmationType.value = "delete";
    confirmationTitle.value = "Delete Class";
    confirmationMessage.value = `This will permanently delete "${cls.name}". This action cannot be undone.`;
    showConfirmationDialog.value = true;
  };

  const handleConfirmation = async (confirmed) => {
    showConfirmationDialog.value = false;
    if (confirmed && confirmationType.value === "delete") {
      await deleteClass();
    }
    classToDelete.value = null;
  };

  const createClass = async () => {
    if (!newClassName.value.trim()) {
      showToast("error", "Please enter a class name.");

      return;
    }

    isSubmitting.value = true;
    try {
      const payload = {
        name: newClassName.value.trim(),
        section: newClassSection.value.trim() || null,
        admissionFee: Math.max(0, Number(newAdmissionFee.value) || 0),
        monthlyFee: Math.max(0, Number(newMonthlyFee.value) || 0),
      };
      await store.dispatch("createClass", payload);
      newClassName.value = "";
      newClassSection.value = "";
      newAdmissionFee.value = 0;
      newMonthlyFee.value = 0;
      showToast("success", `"${payload.name}" has been created successfully!`);
      await store.dispatch("fetchAllClasses");

      // Refresh teacher and student counts
      await refreshTeachersAndStudents();

      switchView("list");
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.message ||
          "Failed to create class. Please try again.",
      );
    } finally {
      isSubmitting.value = false;
    }
  };

  const editClass = async () => {
    if (!editClassName.value.trim()) {
      showToast("error", "Please enter a class name.");

      return;
    }

    isSubmitting.value = true;
    try {
      const updates = {
        name: editClassName.value.trim(),
        section: editClassSection.value.trim() || null,
        admissionFee: Math.max(0, Number(editAdmissionFee.value) || 0),
        monthlyFee: Math.max(0, Number(editMonthlyFee.value) || 0),
        applyToAllStudents: editApplyToAllStudents.value,
      };
      await store.dispatch("editClassById", {
        classId: editingClass.value._id || editingClass.value.id,
        updates,
      });
      showToast("success", `"${updates.name}" has been updated successfully!`);
      await store.dispatch("fetchAllClasses");
      switchView("list");
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.message ||
          "Failed to update class. Please try again.",
      );
    } finally {
      isSubmitting.value = false;
    }
  };

  const deleteClass = async () => {
    isSubmitting.value = true;
    try {
      await store.dispatch(
        "deleteClassById",
        classToDelete.value._id || classToDelete.value.id,
      );
      showToast(
        "success",
        `"${classToDelete.value.name}" has been deleted successfully.`,
      );
      await store.dispatch("fetchAllClasses");
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.message ||
          "Failed to delete class. Please try again.",
      );
    } finally {
      isSubmitting.value = false;
    }
  };

  const handleToggleAll = () => {
    bulkTemplates.value.forEach((t) => (t.selected = bulkSelectAll.value));
  };

  const handleToggleOne = () => {
    const all = bulkTemplates.value.every((t) => t.selected);
    const none = bulkTemplates.value.every((t) => !t.selected);
    bulkSelectAll.value = all && !none;
  };

  // Per-class section helpers
  const addSection = (tpl) => {
    const val = (tpl.newSection || "").trim();
    if (!val) return;
    const exists = tpl.sections.some(
      (s) => s.toLowerCase() === val.toLowerCase(),
    );
    if (!exists) tpl.sections.push(val);
    tpl.newSection = "";
    tpl.adding = false;
  };

  const removeSection = (tpl, idx) => {
    tpl.sections.splice(idx, 1);
  };

  // Custom class row helpers
  const addCustomClassRow = () => {
    customClasses.value.push({ name: "", section: "", monthlyFee: "" });
  };
  const removeCustomClassRow = (idx) => {
    customClasses.value.splice(idx, 1);
  };
  const rowError = (idx) => {
    const r = customClasses.value[idx];
    return r && !(r.name || "").trim();
  };

  const handleBulkCreate = async () => {
    // Build payloads from defaults
    const selected = bulkTemplates.value.filter((t) => t.selected);
    const globalSection = (bulkSection.value || "").trim() || null;
    const globalAdmissionFee = Math.max(0, Number(bulkAdmissionFee.value) || 0);
    const globalMonthlyFee = Math.max(0, Number(bulkMonthlyFee.value) || 0);

    const defaultPayloads = selected.flatMap((t) => {
      if (t.sections.length > 0) {
        return t.sections.map((sec) => ({
          name: t.name,
          section: sec,
          admissionFee: globalAdmissionFee,
          monthlyFee: globalMonthlyFee,
        }));
      }
      return [
        {
          name: t.name,
          section: globalSection,
          admissionFee: globalAdmissionFee,
          monthlyFee: globalMonthlyFee,
        },
      ];
    });

    // Build payloads from custom rows (require name, section optional -> fallback to global)
    const customPayloads = customClasses.value
      .filter((r) => (r.name || "").trim())
      .map((r) => ({
        name: r.name.trim(),
        section: (r.section || "").trim() || globalSection,
        admissionFee: globalAdmissionFee,
        monthlyFee:
          r.monthlyFee !== undefined && r.monthlyFee !== ""
            ? Math.max(0, Number(r.monthlyFee) || 0)
            : globalMonthlyFee,
      }));

    const payloads = [...defaultPayloads, ...customPayloads];

    if (payloads.length === 0) {
      showToast("error", "Please select or add at least one class to create.");
      return;
    }

    // Validate: prevent duplicate empty names etc. (basic)
    const invalidCustom = customClasses.value.some(
      (r) => (r.name || "").trim() === "",
    );
    if (invalidCustom) {
      showToast("error", "Custom classes must have a class name.");

      return;
    }

    isSubmitting.value = true;
    try {
      const results = await Promise.allSettled(
        payloads.map((p) => store.dispatch("createClass", p)),
      );
      const successCount = results.filter(
        (r) => r.status === "fulfilled",
      ).length;
      const failCount = results.length - successCount;

      await store.dispatch("fetchAllClasses");

      const message =
        successCount > 0
          ? `Created ${successCount} class${successCount > 1 ? "es" : ""}${
              failCount ? ` (${failCount} failed)` : ""
            }.`
          : "Failed to create classes. Please try again.";

      showToast(successCount > 0 ? "success" : "error", message);

      // Reset custom rows after success
      if (successCount > 0) {
        customClasses.value = [];
      }

      showEmptyStateCreateDialog.value = false;

      // Refresh teacher and student counts
      await refreshTeachersAndStudents();
      // Check totals after creation
      if (TotalTeachers.value == 0 && TotalStudents.value == 0) {
        router.replace("/onboarding");
        return;
      }
    } catch (error) {
      showToast("error", "Failed to create classes. Please try again.");
    } finally {
      isSubmitting.value = false;
    }
  };
</script>

<style scoped>
  .modal-enter-active {
    transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .modal-leave-active {
    transition: all 0.2s ease-in;
  }
  .modal-enter-from,
  .modal-leave-to {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }
</style>
